import { Nullable } from 'cng-common/models/utils'
import Logger from 'core/utils/Logger'

import { CampaignDocument } from '../../model/campaign'
import { campaignBaseApi } from '../campaignBaseApi'
import {
  buildLinkCreateParams,
  buildLinkDeleteParams,
  buildLinkUpdateParams,
  mapLinkFromRawData,
} from '../mapping'
import { AttachLinkParams, CampaignApiTags, DeleteLinkParams, UpdateLinkParams } from '../models'
import {
  campaignProcedureUrl,
  SP_LUT_CAMPAIGN_LINKS_CREATE,
  SP_LUT_CAMPAIGN_LINKS_DELETE,
  SP_LUT_CAMPAIGN_LINKS_UPDATE,
} from '../procedures'

const linksEndpoints = campaignBaseApi.injectEndpoints({
  endpoints: (build) => ({
    // Attach link
    // Creates a link row via its own stored procedure — links live in a
    // separate table from document attachments, and no file content exists.
    attachLink: build.mutation<Nullable<CampaignDocument>, AttachLinkParams>({
      async queryFn(params, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_LINKS_CREATE),
          method: 'POST',
          data: buildLinkCreateParams(params),
        })

        if (result.error) {
          Logger.error('attachLink: Failed to attach link', result.error)
          return { error: result.error }
        }

        const row = result.data?.Data?.[0]
        return { data: row ? mapLinkFromRawData(row) : null }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignDocuments, id: params.campaignId },
      ],
    }),

    // Update link
    // Links live in their own table with their own stored procedure —
    // a separate entity from document attachments.
    updateLink: build.mutation<Nullable<CampaignDocument>, UpdateLinkParams>({
      async queryFn(params, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_LINKS_UPDATE),
          method: 'POST',
          data: buildLinkUpdateParams(params),
        })

        if (result.error) {
          Logger.error('updateLink: Failed to update link', result.error)
          return { error: result.error }
        }

        const row = result.data?.Data?.[0]
        return { data: row ? mapLinkFromRawData(row) : null }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignDocuments, id: params.campaignId },
      ],
    }),

    // Delete link
    // Links live in their own table with their own stored procedure —
    // a separate entity from document attachments.
    deleteLink: build.mutation<boolean, DeleteLinkParams>({
      async queryFn(link, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_LINKS_DELETE),
          method: 'POST',
          data: buildLinkDeleteParams(link),
        })

        if (result.error) {
          Logger.error('deleteLink: Failed to delete link', result.error)
          return { error: result.error }
        }

        return { data: true }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignDocuments, id: params.campaignId },
      ],
    }),
  }),
})

export const { useAttachLinkMutation, useUpdateLinkMutation, useDeleteLinkMutation } =
  linksEndpoints
